import { useEffect, useState } from 'react'
import { convertXmlToJson } from '@which/shared'

import { testFileSize } from './config/test-file-size'
import { toolComponentMap } from './config/tool-component-map'
import { xmlDataValidation } from './config/tool-data-format'

export const WhichToolWithDataEntryPoint = ({
  toolName,
  xmlFileSize,
  xmlData,
  xmlFilePath,
}: WhichToolWithDataEntryPointProps) => {
  const [decompressedXml, setDecompressedXml] = useState<XmlDataSet | null>(null)
  const isCompressed = !!(!xmlData && xmlFilePath)

  useEffect(() => {
    if (isCompressed) {
      const fetchCompressedXmlData = async () => {
        try {
          const response = await fetch(xmlFilePath)

          if (!response.ok) {
            throw new Error(`Gzip Fetch HTTP error! status: ${response.status}`)
          }

          const parsedData = await decompressCompressedXml(response)

          parsedData && setDecompressedXml(convertXmlToJson(parsedData))
        } catch (error) {
          console.error('Failed to load gzip data file for static tool', error)
          return null
        }
      }

      fetchCompressedXmlData()
    }
  }, [xmlFilePath, isCompressed])

  if (!toolName || !xmlFileSize || (!xmlData && !xmlFilePath)) {
    return null
  }

  const toolConfig = toolComponentMap[toolName]
  if (!toolConfig) {
    console.warn(`No component mapping found for tool: ${toolName}`)
    return null
  }

  const { component: ToolToRender, dataValidation, isRecordValid } = toolConfig

  const toolData = isCompressed ? decompressedXml : xmlData

  const isXmlDataValid = xmlDataValidation({ xmlData: toolData, dataValidation, isRecordValid })
  const isFileSizeOk: boolean = testFileSize(xmlFileSize)

  if (!ToolToRender || !isFileSizeOk || !isXmlDataValid || !toolData) {
    return null
  }

  return (
    <div data-testid="tool-with-data-container">
      <ToolToRender xmlData={toolData} />
    </div>
  )
}

const decompressCompressedXml = async (response: Response): Promise<string> => {
  try {
    const ds = new DecompressionStream('gzip')
    const blob_in = await response.blob()
    const stream_in = blob_in.stream().pipeThrough(ds)
    const blob_out = new Response(stream_in)
    const data = await blob_out.text()

    return data
  } catch (error) {
    return Promise.reject(`Failed to decompress gzipped XML file: ${error}`)
  }
}

type XmlRecord = {
  [key: string]: string
}

type XmlDataSet = XmlRecord[]

type WhichToolWithDataEntryPointProps = {
  toolName?: string
  xmlFileSize?: number
  xmlData?: XmlDataSet
  xmlFilePath?: string
}
